import React, { useRef, useEffect, useState, useMemo } from "react";
import { format } from "date-fns";
import styles from "./NotesPage.module.css";

export interface Note {
  _id: string;
  noteCategory: "clinical" | "non-clinical";
  richText: string;
  createdTimestamp: number;
  author?: string;
  isImportant?: "yes" | "no";
}

interface NotesPageProps {
  notes?: Note[];
  loading?: boolean;
  customHeight?: string;
  isPdf?: boolean;
  patientId?: string;
  startingIndex?: number;
  patientBillingMinuteMessage?: string;
}

interface SliceNote {
  note: Note;
  isSlice: boolean;
  showHeader: boolean;
  sliceHtml?: string;
  sliceHeight?: number;
}

interface NotePage {
  items: SliceNote[];
}

const NotesPage: React.FC<NotesPageProps> = ({
  notes = [],
  loading = false,
  customHeight = "11in",
  isPdf = false,
  patientBillingMinuteMessage,
}) => {
  // page height
  // const PAGE_HEIGHT = useMemo(() => {
  //   return parseFloat(customHeight) * 96 - 80;
  // }, [customHeight]);

  const [pages, setPages] = useState<NotePage[]>([]);
  const measureRef = useRef<HTMLDivElement>(null);

  // useEffect(() => {
  //   if (!measureRef.current || loading) {
  //     setPages([]);
  //     return;
  //   }

  //   const hidden = measureRef.current;
  //   const nodes = Array.from(hidden.children) as HTMLElement[];

  //   const resultPages: NotePage[] = [];
  //   let isFirstPage = true;
  //   let currentPage: SliceNote[] = [];
  //   let currentHeight = 0;

  //   notes.forEach((note, i) => {
  //     const node = nodes[i];

  //     const rect = node.getBoundingClientRect();
  //     const style = window.getComputedStyle(node);
  //     const marginTop = parseFloat(style.marginTop || "0");
  //     const marginBottom = parseFloat(style.marginBottom || "0");
  //     const fullHeight = rect.height + marginTop + marginBottom;

  //     // full note
  //     if (fullHeight <= PAGE_HEIGHT) {
  //       const subHeaderHeight = patientBillingMinuteMessage ? 48 : 0;
  //       const pageLimit = PAGE_HEIGHT - (isFirstPage ? subHeaderHeight : 0);

  //       if (currentHeight + fullHeight > pageLimit) {
  //         resultPages.push({ items: [...currentPage] });
  //         currentPage = [];
  //         currentHeight = 0;
  //         isFirstPage = false;
  //       }

  //       currentPage.push({
  //         note,
  //         isSlice: false,
  //         showHeader: true,
  //       });

  //       currentHeight += fullHeight;
  //       return;
  //     }

  //     // big note slicing
  //     const bodyClass = CSS.escape(styles.noteBody);
  //     const headerClass = CSS.escape(styles.noteHeader);

  //     const bodyEl = node.querySelector(`.${bodyClass}`) as HTMLElement;
  //     const headerEl = node.querySelector(`.${headerClass}`) as HTMLElement;

  //     const headerRect = headerEl?.getBoundingClientRect();
  //     const headerHeight = headerRect ? headerRect.height : 0;

  //     const blocks = Array.from(bodyEl.children) as HTMLElement[];
  //     let blockIndex = 0;
  //     let firstSlice = true;

  //     while (blockIndex < blocks.length) {
  //       let sliceBlocks: HTMLElement[] = [];
  //       let sliceHeight = 0;

  //       while (blockIndex < blocks.length) {
  //         const el = blocks[blockIndex];
  //         const r = el.getBoundingClientRect();
  //         const cs = window.getComputedStyle(el);
  //         const mtop = parseFloat(cs.marginTop || "0");
  //         const mbot = parseFloat(cs.marginBottom || "0");

  //         const h = r.height + mtop + mbot;
  //         const subHeaderHeight = patientBillingMinuteMessage ? 48 : 0;

  //         const available = firstSlice
  //           ? PAGE_HEIGHT - headerHeight - (isFirstPage ? subHeaderHeight : 0)
  //           : PAGE_HEIGHT - (isFirstPage ? subHeaderHeight : 0);

  //         if (sliceHeight + h > available) break;

  //         sliceBlocks.push(el);
  //         sliceHeight += h;
  //         blockIndex++;
  //       }

  //       if (sliceBlocks.length === 0) {
  //         if (currentHeight > 0) {
  //           resultPages.push({ items: [...currentPage] });
  //           currentPage = [];
  //           currentHeight = 0;
  //           isFirstPage = false;
  //         }

  //         const el = blocks[blockIndex];

  //         currentPage.push({
  //           note,
  //           isSlice: true,
  //           showHeader: firstSlice,
  //           sliceHtml: el.outerHTML,
  //           sliceHeight: PAGE_HEIGHT,
  //         });

  //         blockIndex++;
  //         firstSlice = false;
  //         continue;
  //       }

  //       if (currentHeight + sliceHeight > PAGE_HEIGHT) {
  //         resultPages.push({ items: [...currentPage] });
  //         currentPage = [];
  //         currentHeight = 0;
  //         isFirstPage = false;
  //       }

  //       const temp = document.createElement("div");
  //       sliceBlocks.forEach((b) => temp.appendChild(b.cloneNode(true)));

  //       currentPage.push({
  //         note,
  //         isSlice: true,
  //         showHeader: firstSlice,
  //         sliceHtml: temp.innerHTML,
  //         sliceHeight,
  //       });

  //       currentHeight += sliceHeight;
  //       firstSlice = false;
  //     }
  //   });

  //   if (currentPage.length > 0) {
  //     resultPages.push({ items: [...currentPage] });
  //   }

  //   setPages(resultPages);
  // }, [notes, loading, customHeight, PAGE_HEIGHT]);

  useEffect(() => {
    if (!measureRef.current || loading) {
      setPages([]);
      return;
    }

    const hidden = measureRef.current;
    const nodes = Array.from(hidden.children) as HTMLElement[];

    const resultPages: NotePage[] = [];
    let currentPage: SliceNote[] = [];
    let currentHeight = 0;
    const subHeaderHeight = patientBillingMinuteMessage ? 48 : 0;
    let isFirstPage = true;

    const PAGE_HEIGHT_PX = 10.5 * 96;
    const BOTTOM_RESERVE_PX = 32;
    const CONTENT_PAGE_HEIGHT = PAGE_HEIGHT_PX - BOTTOM_RESERVE_PX;

    const getPageLimit = () =>
      CONTENT_PAGE_HEIGHT - (isFirstPage ? subHeaderHeight : 0);

    const pushNewPage = () => {
      if (currentPage.length > 0) {
        resultPages.push({ items: [...currentPage] });
      }
      currentPage = [];
      currentHeight = 0;
      isFirstPage = false;
    };

    notes.forEach((note, i) => {
      const node = nodes[i];

      const rect = node.getBoundingClientRect();
      const style = window.getComputedStyle(node);

      const fullHeight =
        rect.height +
        parseFloat(style.marginTop || "0") +
        parseFloat(style.marginBottom || "0");

      // FULL NOTE
      if (fullHeight <= CONTENT_PAGE_HEIGHT) {
        if (currentHeight + fullHeight > getPageLimit()) {
          pushNewPage();
        }

        currentPage.push({
          note,
          isSlice: false,
          showHeader: true,
        });

        currentHeight += fullHeight;
        return;
      }
      // big note
      const bodyClass = CSS.escape(styles.noteBody);
      const headerClass = CSS.escape(styles.noteHeader);

      const bodyEl = node.querySelector(`.${bodyClass}`) as HTMLElement;
      const headerEl = node.querySelector(`.${headerClass}`) as HTMLElement;

      const headerHeight = headerEl.getBoundingClientRect().height;
      const blocks = Array.from(bodyEl.children) as HTMLElement[];

      let blockIndex = 0;
      let firstSlice = true;

      while (blockIndex < blocks.length) {
        let sliceBlocks: HTMLElement[] = [];
        let sliceHeight = 0;

        const available =
          CONTENT_PAGE_HEIGHT -
          (firstSlice ? headerHeight : 0) -
          (isFirstPage ? subHeaderHeight : 0);

        while (blockIndex < blocks.length) {
          const el = blocks[blockIndex];
          const r = el.getBoundingClientRect();
          const cs = window.getComputedStyle(el);

          const h =
            r.height +
            parseFloat(cs.marginTop || "0") +
            parseFloat(cs.marginBottom || "0");

          if (sliceHeight + h > available) break;

          sliceBlocks.push(el);
          sliceHeight += h;
          blockIndex++;
        }

        const totalSliceHeight = sliceHeight + (firstSlice ? headerHeight : 0);

        if (currentHeight + totalSliceHeight > getPageLimit()) {
          pushNewPage();
        }

        const temp = document.createElement("div");
        sliceBlocks.forEach((b) => temp.appendChild(b.cloneNode(true)));

        currentPage.push({
          note,
          isSlice: true,
          showHeader: firstSlice,
          sliceHtml: temp.innerHTML,
        });

        currentHeight += totalSliceHeight;
        firstSlice = false;
      }
    });

    if (currentPage.length > 0) {
      resultPages.push({ items: currentPage });
    }

    setPages(resultPages);
  }, [notes, loading, patientBillingMinuteMessage]);

  const TIMEZONE = "America/New_York";

  const formatDateByTimezone = (timestamp: number) => {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: TIMEZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(timestamp));
  };

  if (loading) {
    return (
      <div className={styles.content} style={{ height: customHeight }}>
        <div className={styles.pageContainer}>
          <div className={styles.contentInner}>
            <div className={styles.sectionHeader}>PATIENT NOTES</div>
            <div className={styles.loading}>Loading notes...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {pages.map((page, pageIndex) => (
        <div
          key={pageIndex}
          className={styles.content}
          style={{
            height: customHeight,
            pageBreakAfter: pageIndex === pages.length - 1 ? "avoid" : "always",
            pageBreakInside: "avoid",
            marginBottom: isPdf ? "0" : "1rem",
          }}
        >
          <div className={styles.pageContainer}>
            <div className={styles.contentInner}>
              <div className={styles.sectionHeader}>PATIENT NOTES</div>
              {pageIndex === 0 && patientBillingMinuteMessage && (
                <div
                  style={{
                    fontSize: "1.4rem",
                    fontWeight: 500,
                    margin: "1rem 0",
                    height: "3rem",
                    backgroundColor: "#e5e7eb",
                  }}
                >
                  <div style={{ marginTop: "0.3rem", marginLeft: "1rem" }}>
                    {patientBillingMinuteMessage}
                  </div>
                </div>
              )}

              <div className={styles.notesList}>
                {page.items.length > 0 ? (
                  page.items.map((item, idx) => (
                    <div
                      key={`${item.note._id}-${idx}`}
                      className={styles.noteCard}
                      style={
                        item.note.isImportant === "yes"
                          ? { border: "2px solid red" }
                          : {}
                      }
                    >
                      {item.showHeader && (
                        <div className={styles.noteHeader}>
                          {item.note.noteCategory === "clinical"
                            ? "Clinical Note"
                            : "Internal Note"}
                          {" - "}
                          {item.note.createdTimestamp
                            ? formatDateByTimezone(item.note.createdTimestamp)
                            : "N/A"}
                        </div>
                      )}

                      <div
                        className={styles.noteBody}
                        dangerouslySetInnerHTML={{
                          __html: item.sliceHtml || item.note.richText,
                        }}
                      />
                    </div>
                  ))
                ) : (
                  <div className={styles.noData}>
                    {loading
                      ? "Loading notes..."
                      : "No notes available for this billing cycle."}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}

      {isPdf && <div style={{ height: "1in", visibility: "hidden" }} />}

      {/* hidden measure div */}
      <div
        ref={measureRef}
        style={{
          position: "absolute",
          visibility: "hidden",
          width: "8in",
          left: "-9999px",
          top: "-9999px",
        }}
      >
        {notes.map((note) => (
          <div
            key={`measure-${note._id}`}
            className={styles.noteCard}
            style={
              note.isImportant === "yes" ? { border: "2px solid red" } : {}
            }
          >
            <div className={styles.noteHeader}>
              {note.noteCategory === "clinical"
                ? "Clinical Note"
                : "Internal Note"}
              {" - "}
              {note.createdTimestamp
                ? formatDateByTimezone(note.createdTimestamp)
                : "N/A"}
            </div>
            <div
              className={styles.noteBody}
              dangerouslySetInnerHTML={{
                __html: note.richText,
              }}
            />
          </div>
        ))}
      </div>
    </>
  );
};

export default NotesPage;
